import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import {
	EmailTemplateScopeKind,
	type EmailTemplateScope,
} from '@dealstack/frontend/shared/components/EmailTemplates/EmailTemplates.types';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	PreviewEmailTemplateDto,
	EmailTemplateDetailRes,
	EmailTemplatePreviewRes,
	EmailTemplateSummaryRes,
	UpdateEmailTemplateDto,
} from '@dealstack/shared/lib/generated';

const ENDPOINTS = {
	[EmailTemplateScopeKind.PROGRAMME]: {
		list: API_PATHS.PROGRAMME_EMAIL_TEMPLATE_GET,
		detail: API_PATHS.PROGRAMME_EMAIL_TEMPLATE_TYPE_GET,
		update: API_PATHS.PROGRAMME_EMAIL_TEMPLATE_TYPE_PATCH,
		reset: API_PATHS.PROGRAMME_EMAIL_TEMPLATE_TYPE_RESET_POST,
		preview: API_PATHS.PROGRAMME_EMAIL_TEMPLATE_TYPE_PREVIEW_POST,
	},
	[EmailTemplateScopeKind.DEAL]: {
		list: API_PATHS.DEAL_EMAIL_TEMPLATE_GET,
		detail: API_PATHS.DEAL_EMAIL_TEMPLATE_TYPE_GET,
		update: API_PATHS.DEAL_EMAIL_TEMPLATE_TYPE_PATCH,
		reset: API_PATHS.DEAL_EMAIL_TEMPLATE_TYPE_RESET_POST,
		preview: API_PATHS.DEAL_EMAIL_TEMPLATE_TYPE_PREVIEW_POST,
	},
	[EmailTemplateScopeKind.ORGANIZATION]: {
		list: API_PATHS.PROGRAMME_DEFAULTS_EMAIL_TEMPLATE_ORGANIZATIONID_GET,
		detail: API_PATHS.PROGRAMME_DEFAULTS_EMAIL_TEMPLATE_ORGANIZATIONID_TYPE_GET,
		update: API_PATHS.PROGRAMME_DEFAULTS_EMAIL_TEMPLATE_ORGANIZATIONID_TYPE_PATCH,
		reset: API_PATHS.PROGRAMME_DEFAULTS_EMAIL_TEMPLATE_ORGANIZATIONID_TYPE_RESET_POST,
		preview: API_PATHS.PROGRAMME_DEFAULTS_EMAIL_TEMPLATE_ORGANIZATIONID_TYPE_PREVIEW_POST,
	},
	[EmailTemplateScopeKind.ORGANIZATION_DEAL]: {
		list: API_PATHS.DEAL_DEFAULTS_EMAIL_TEMPLATE_ORGANIZATIONID_GET,
		detail: API_PATHS.DEAL_DEFAULTS_EMAIL_TEMPLATE_ORGANIZATIONID_TYPE_GET,
		update: API_PATHS.DEAL_DEFAULTS_EMAIL_TEMPLATE_ORGANIZATIONID_TYPE_PATCH,
		reset: API_PATHS.DEAL_DEFAULTS_EMAIL_TEMPLATE_ORGANIZATIONID_TYPE_RESET_POST,
		preview: API_PATHS.DEAL_DEFAULTS_EMAIL_TEMPLATE_ORGANIZATIONID_TYPE_PREVIEW_POST,
	},
} as const satisfies Record<EmailTemplateScopeKind, Record<string, string>>;

/**
 * Programme and deal ids travel as headers, so their paths have no placeholders and this is a
 * no-op for them; only the organisation routes carry the id in the path.
 */
const endpointFor = (
	scope: EmailTemplateScope,
	key: keyof (typeof ENDPOINTS)[EmailTemplateScopeKind],
	params: Record<string, string> = {}
) => formatUnicorn(ENDPOINTS[scope.kind][key], { organizationId: scope.id, ...params });

export const listEmailTemplates = (scope: EmailTemplateScope) =>
	makeRequest<ApiV1.Response<EmailTemplateSummaryRes[]>>({
		endpoint: endpointFor(scope, 'list'),
		method: HTTP_METHOD.GET,
	});

export const getEmailTemplate = (scope: EmailTemplateScope, type: string) =>
	makeRequest<ApiV1.Response<EmailTemplateDetailRes>>({
		endpoint: endpointFor(scope, 'detail', { type }),
		method: HTTP_METHOD.GET,
	});

export const updateEmailTemplate = (
	scope: EmailTemplateScope,
	type: string,
	body: UpdateEmailTemplateDto
) =>
	makeRequest<ApiV1.Response<EmailTemplateDetailRes>>({
		endpoint: endpointFor(scope, 'update', { type }),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});

export const resetEmailTemplate = (scope: EmailTemplateScope, type: string) =>
	makeRequest<ApiV1.Response<EmailTemplateDetailRes>>({
		endpoint: endpointFor(scope, 'reset', { type }),
		method: HTTP_METHOD.POST,
	});

export const previewEmailTemplate = (
	scope: EmailTemplateScope,
	type: string,
	body: PreviewEmailTemplateDto
) =>
	makeRequest<ApiV1.Response<EmailTemplatePreviewRes>>({
		endpoint: endpointFor(scope, 'preview', { type }),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});
